import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import igaBadge from "../../../assets/iga-certificates-badge.png";
import igaLogo from "../../../assets/iga-logo.png";
import poweredBySherpa from "../../../assets/powered-by-sherpa.png";
import { UserCertificateResult } from "../../../services/api/types/certificates/certificates";
import { useAuth } from "../../../context/AuthContext";
import { useCustomisation } from "../../../context/CustomisationContext";
import { resolveAssetUrl } from "../../../utils/helpers";

interface CertificateViewProps {
  certificate: UserCertificateResult;
  recipientName?: string;
}

export const CertificateView: React.FC<CertificateViewProps> = ({
  certificate,
  recipientName: recipientNameProp,
}) => {
  const { auth } = useAuth();
  const { getCustomisationValue } = useCustomisation();

  const certificateLogoUrl = resolveAssetUrl(
    getCustomisationValue("CERTIFICATES", "logoSettings", "certificatesLogo"),
  );

  // Format completion date as DD/MMM/YYYY (e.g., 04/Nov/2025)
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Format time spent (number in minutes to string)
  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min${minutes !== 1 ? "s" : ""}`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins > 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""} ${mins} min${
        mins !== 1 ? "s" : ""
      }`;
    }
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  };

  const formattedDate = formatDate(certificate.completionDate);
  const duration = formatDuration(certificate.timeSpent);
  const recipientName =
    recipientNameProp ||
    (auth.user?.firstName && auth.user?.lastName
      ? `${auth.user.firstName} ${auth.user.lastName}`
      : auth.user?.email || "User");

  const titleColor = "#262653";

  return (
    <Box
      id={certificate.certificateUid}
      sx={{
        // CANVAS (1700x1200)
        width: "1700px",
        height: "1200px",
        backgroundColor: "#F3F3F3",
        p: 50,
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Black frame (1600x1100) */}
      <Box
        sx={{
          width: "1600px",
          height: "1100px",
          border: "5px solid #202451",
          borderRadius: "20px",
          backgroundColor: "#F3F3F3",
          boxSizing: "border-box",
          display: "flex",

          // padding between black frame and white frame
          p: "50px",
        }}
      >
        {/* White rounded rectangle (1500x1000) */}
        <Box
          sx={{
            width: "1500px",
            height: "1000px",
            backgroundColor: "#fff",
            borderRadius: "20px",
            p: "90px 100px 80px 100px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {/* Top row */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box
              component="img"
              src={certificateLogoUrl || igaLogo}
              alt="iGaming Academy"
              crossOrigin={certificateLogoUrl ? "anonymous" : undefined}
              sx={{
                height: 85,
                width: "auto",
                maxWidth: 340,
                objectFit: "contain",
              }}
            />
          </Stack>

          {/* Center content */}
          <Stack alignItems="center" sx={{ textAlign: "center", mt: 4 }}>
            <Typography
              sx={{
                color: titleColor,
                fontSize: 36,
                fontWeight: 400,
                mb: 2.5,
              }}
            >
              Certificate of Completion
            </Typography>

            <Typography
              sx={{
                color: titleColor,
                fontSize: 72,
                fontWeight: 800,
                mb: 1.75,
              }}
            >
              {recipientName}
            </Typography>

            <Typography
              sx={{
                color: titleColor,
                fontSize: 24,
                fontWeight: 400,
                mb: 3.5,
              }}
            >
              has successfully attained competency in
            </Typography>

            <Typography
              sx={{
                color: titleColor,
                fontSize: 45,
                fontWeight: 400,
                lineHeight: 1.25,
                maxWidth: "80%",
                mb: 2.5,
              }}
            >
              {certificate.assignment}
            </Typography>

            <Typography
              sx={{
                color: titleColor,
                fontSize: 24,
                fontWeight: 400,
              }}
            >
              {formattedDate}
            </Typography>
          </Stack>

          {/* Bottom row */}
          <Stack direction="row" alignItems="center" sx={{ mt: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Stack spacing={0.5}>
                {certificate.bestScore !== 0 && (
                  <Typography
                    sx={{
                      color: titleColor,
                      fontSize: 13,
                      fontStyle: "italic",
                      fontWeight: 300,
                    }}
                  >
                    Score: {certificate.bestScore}%
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: titleColor,
                    fontSize: 13,
                    fontStyle: "italic",
                    fontWeight: 300,
                  }}
                >
                  Duration: {duration}
                </Typography>
                <Typography
                  sx={{
                    color: titleColor,
                    fontSize: 13,
                    fontStyle: "italic",
                    fontWeight: 300,
                    wordBreak: "break-all",
                    maxWidth: 260,
                  }}
                >
                  Certificate ID: {certificate.certificateUid}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={igaBadge}
                alt="Badge"
                sx={{
                  width: 123,
                  height: 123,
                  borderRadius: "999px",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              <Box
                component="img"
                src={poweredBySherpa}
                alt="Powered by Sherpa"
                sx={{
                  height: 45,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
